/**
 * 상세 다이얼로그 정보 패널의 값 — 카드 원본(광고 `card.ad` + 광고 원본 문서 · 오가닉 `card.post`)에서 사람이 바로 읽을 것만 뽑는다.
 *
 * 세 덩어리로 돌려준다: 성과 숫자(`metrics`, 오가닉) · 핵심 사실(`facts`, 이름표 + 값) · 태그 묶음(`tags`). 값이 빈 것은 뺀다.
 * 판별 근거·신뢰도·내부 코드 같은 분석용 값은 싣지 않는다.
 * ⚠️ 광고 원본 문서는 다이얼로그를 열 때 받는다 — 오기 전에는 목록 값(`card.ad`)만으로 채운다.
 */


import { fNumber } from 'src/utils/format/format-number';
import { adRunDays } from 'src/utils/ads-meta/meta-ads-format';
import { engagementRatePct } from 'src/utils/metrics/engagement-rate';
import { formatNumber, formatTimestamp } from 'src/utils/format/format-utils';
import { channelLabel, countryLabel } from 'src/utils/platform/channel-label';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { readVideoMetas, formatVideoDuration } from 'src/utils/moments/view/post-media';

import { contentLanguageMap } from 'src/constants/content-languages';
import { PRODUCT_SOURCE_LABELS } from 'src/constants/product-sources';
import {
  pickAdLabels,
  AD_CTA_LABELS,
  AD_CLAIM_LABELS,
  AD_OFFER_LABELS,
  AD_FORMAT_LABELS,
  AD_LANDING_PLATFORM_LABELS,
  AD_DELIVERY_SEGMENT_LABELS,
} from 'src/constants/ad-labels';

import type { MediaCard } from './search-all-media-cards';
import type { MetricBasis } from './search-all-detail-options';

export type DetailMetric = { label: string; value: string };
export type DetailFact = { label: string; value: string };
export type DetailTagGroup = { title: string; tags: string[] };
export type DetailPanel = { metrics: DetailMetric[]; facts: DetailFact[]; tags: DetailTagGroup[] };

const FORMAT_LABEL = pickAdLabels(AD_FORMAT_LABELS, 'short');
const LANDING_LABEL = pickAdLabels(AD_LANDING_PLATFORM_LABELS, 'short');
const OFFER_LABEL = pickAdLabels(AD_OFFER_LABELS, 'full');
const CTA_LABEL = pickAdLabels(AD_CTA_LABELS, 'full');
const CLAIM_LABEL = pickAdLabels(AD_CLAIM_LABELS, 'full');
const SEGMENT_LABEL = pickAdLabels(AD_DELIVERY_SEGMENT_LABELS, 'full');
const LANGUAGE_LABEL: Record<string, string> = Object.fromEntries(contentLanguageMap.map((l) => [l.code, l.name]));

/** 광고 노출 매체 표기 */
const PUBLISHER_LABEL: Record<string, string> = { FACEBOOK: 'Facebook', INSTAGRAM: 'Instagram', MESSENGER: 'Messenger', AUDIENCE_NETWORK: 'Audience Network', THREADS: 'Threads', WHATSAPP: 'WhatsApp' };

function text(v: unknown): string {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
}

function list(v: unknown): string[] {
  if (Array.isArray(v)) return [...new Set(v.map(text).filter(Boolean))];
  const one = text(v);
  return one ? [one] : [];
}

function num(v: unknown): number | null {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) ? n : null;
}

function obj(v: unknown): Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** `20260901` · ISO → `2026.09.01` */
function dateLabel(v: unknown): string {
  const s = text(v);
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6, 8)}`;
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

/** 코드 → 이름표 · 모르는 코드는 버린다 */
function labels(codes: string[], table: Record<string, string>): string[] {
  return codes.map((c) => table[c]).filter((v): v is string => Boolean(v));
}

function facts(rows: DetailFact[]): DetailFact[] {
  return rows.filter((r) => r.value !== '');
}

function tagGroups(groups: DetailTagGroup[]): DetailTagGroup[] {
  return groups.filter((g) => g.tags.length > 0);
}

/**
 * @param card 열린 카드
 * @param adDoc 광고 원본 문서(`ads.detail` 의 `esDoc`) — 아직 없으면 `null`
 * @param metricBasis 오가닉 수치를 읽을 시점 — 없으면 색인 루트 값. 주면 「수치 갱신」 사실 칸이 붙는다
 * @returns 상품 카드는 빈 패널 — 상품 상세는 상품 정보 조사의 상세 본문이 그린다
 */
export function buildDetailPanel(card: MediaCard, adDoc: Record<string, unknown> | null, metricBasis?: MetricBasis): DetailPanel {
  if (card.kind === 'product') return productPanel(card);
  return card.kind === 'ad' ? adPanel(card, adDoc ?? {}) : organicPanel(card, metricBasis);
}

/** 오가닉 수치 묶음 — 시점을 고르면 그 스냅샷(`getMetricsByScoreTime`), 아니면 루트 값 */
function organicMetrics(post: Record<string, unknown>, basis: MetricBasis | undefined) {
  if (!basis) {
    return {
      views: num(post.viewCount),
      likes: num(post.likeCount),
      comments: num(post.commentCount),
      shares: num(post.shareCount),
      saves: num(post.savedCount),
      downloads: num(post.downloadCount),
      updatedAt: '',
    };
  }
  const snap = getMetricsByScoreTime(post as Parameters<typeof getMetricsByScoreTime>[0], { useMetricDetailSort: basis === 'sort' });
  return {
    views: num(snap.viewCount),
    likes: num(snap.likeCount),
    comments: num(snap.commentCount),
    shares: num(snap.shareCount),
    saves: num(snap.savedCount),
    downloads: num(snap.downloadCount),
    updatedAt: snap.requestDateTime === '' || snap.requestDateTime == null ? '' : formatTimestamp(snap.requestDateTime),
  };
}

/**
 * 상품의 **기본 사실** — 카드가 이미 쥔 값만 쓴다(더 묻지 않는다).
 *
 * 🔑 앱 안에서는 이 자리에 상세 본문(`ProductDetailBody`)이 훨씬 많은 것을 편다 —
 *   그래서 다이얼로그는 **상세 본문이 없을 때만** 이 사실들을 적는다.
 * ⚠️ 공유 페이지(비회원)가 그 「없을 때」다 — 상세는 관문 뒤 API 라 안 온다. 그때 이 칸이
 *   비어 있으면 다이얼로그가 통째로 빈 상자로 보인다.
 */
function productPanel(card: MediaCard): DetailPanel {
  const item = card.product;
  if (!item) return { metrics: [], facts: [], tags: [] };
  return {
    metrics: [],
    facts: facts([
      { label: '판매몰', value: PRODUCT_SOURCE_LABELS[item.source] ?? item.source },
      { label: '순위', value: item.rank != null ? `${fNumber(item.rank)}위` : '' },
      { label: '브랜드', value: item.brand },
      { label: '판매가', value: card.cta },
      { label: '할인율', value: card.landing },
      { label: '별점', value: item.starCount ? item.starCount.toFixed(1) : '' },
      { label: '리뷰', value: item.commentCount ? formatNumber(item.commentCount) : '' },
      { label: '수집일', value: dateLabel(item.date) },
    ]),
    tags: [],
  };
}

function adPanel(card: MediaCard, doc: Record<string, unknown>): DetailPanel {
  const ad = card.ad;
  if (!ad) return { metrics: [], facts: [], tags: [] };
  const days = adRunDays(doc.adLifespanDays ?? ad.lifespanDays, ad.durationSeconds);
  const start = dateLabel(ad.startDate) || dateLabel(doc.firstSeenDate);
  const publishers = list(doc.publisherPlatform).map((p) => PUBLISHER_LABEL[p.toUpperCase()] ?? p);
  const format = FORMAT_LABEL[text(doc.displayFormat).toUpperCase()] ?? (ad.mediaType === 'video' ? '영상' : ad.mediaType === 'carousel' ? '캐러셀' : ad.mediaType ? '이미지' : '');
  const landingPlatform = LANDING_LABEL[text(doc.landingPlatform) || ad.landingPlatform] ?? '';
  const segment = SEGMENT_LABEL[list(doc.deliverySegment)[0] ?? ''] ?? '';
  const runner = ad.adDeliveryType === 'partnership' && ad.partnerPageName ? `파트너십 · ${ad.partnerPageName}` : segment;

  return {
    metrics: [],
    facts: facts([
      { label: '집행', value: `${ad.isActive ? '진행 중' : '종료'} · ${fNumber(days)}일` },
      { label: '게재 기간', value: start ? `${start} ~ ${ad.isActive ? '진행 중' : dateLabel(ad.endDate) || '종료'}` : '' },
      { label: '노출 국가', value: countryLabel(ad.adCountry) },
      { label: '노출 매체', value: publishers.join(' · ') },
      { label: '형식', value: [format, ad.mediaCount > 1 ? `${fNumber(ad.mediaCount)}컷` : ''].filter(Boolean).join(' · ') },
      { label: '같은 소재', value: ad.collationCount > 1 ? `${fNumber(ad.collationCount)}개 변형` : '' },
      { label: '랜딩', value: [landingPlatform, ad.landingDomain].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ') },
      { label: '광고 주체', value: runner },
      { label: '게시자', value: [ad.publisherName || ad.pageName, ad.pageVanity ? `@${ad.pageVanity}` : ''].filter(Boolean).join(' · ') },
      { label: '광고주', value: ad.advertiserPageName && ad.advertiserPageName !== (ad.publisherName || ad.pageName) ? ad.advertiserPageName : '' },
      { label: '페이지 분류', value: ad.pageCategories.join(', ') },
      { label: '페이지 팔로워', value: ad.pageLikeCount ? formatNumber(ad.pageLikeCount) : '' },
      { label: 'CTA', value: CTA_LABEL[ad.ctaType] ?? ad.ctaText },
      { label: '노출 규모', value: ad.lowImpressions === true ? '100회 미만' : '' },
      { label: '광고 ID', value: ad.id },
    ]),
    tags: tagGroups([
      { title: '혜택', tags: labels(list(doc.offerType), OFFER_LABEL) },
      { title: '효능 주장', tags: labels(list(doc.claimFlags), CLAIM_LABEL) },
      { title: '피부 고민', tags: list(doc.adConcerns) },
      { title: '언급 브랜드', tags: list(ad.mentionedBrands) },
    ]),
  };
}

function organicPanel(card: MediaCard, basis?: MetricBasis): DetailPanel {
  const post = card.post ?? {};
  const { views, likes, comments, shares, saves, downloads, updatedAt } = organicMetrics(post, basis);
  const language = text(post.tL) || text(post.language);
  const rawDate = text(post.postDate) || text(post.date) || text(post.timestamp);
  const engagement = likes != null || comments != null ? engagementRatePct(likes, comments, views) : null;

  return {
    metrics: [
      { label: '조회', value: views != null ? formatNumber(views) : '' },
      { label: '좋아요', value: likes != null ? formatNumber(likes) : '' },
      { label: '댓글', value: comments != null ? formatNumber(comments) : '' },
      { label: '공유', value: shares ? formatNumber(shares) : '' },
      { label: '저장', value: saves ? formatNumber(saves) : '' },
      { label: '다운로드', value: downloads ? formatNumber(downloads) : '' },
      { label: '반응률', value: engagement != null ? `${engagement < 10 ? engagement.toFixed(1) : Math.round(engagement)}%` : '' },
    ].filter((m) => m.value !== ''),
    facts: facts([
      { label: '채널', value: channelLabel(card.channel) },
      { label: '계정', value: text(post.account) ? `@${text(post.account)}` : '' },
      { label: '팔로워', value: card.followers != null ? formatNumber(card.followers) : '' },
      { label: '게시일', value: dateLabel(rawDate) },
      { label: '수치 갱신', value: updatedAt },
      { label: '시장', value: countryLabel(text(post.market)) },
      { label: '언어', value: language ? LANGUAGE_LABEL[language] ?? language : '' },
      { label: '재생 시간', value: formatVideoDuration(readVideoMetas(post)[0]?.duration) },
      { label: '음악', value: readVideoMetas(post)[0]?.music ?? '' },
      { label: 'AI 생성 영상', value: readVideoMetas(post)[0]?.isGenAi === true ? '예' : '' },
    ]),
    tags: tagGroups([
      { title: '브랜드', tags: list(post.canonicalBrands) },
      { title: '제품', tags: list(obj(post).products).slice(0, 10) },
      { title: '피부 고민', tags: list(post.canonicalConcerns) },
      { title: '성분', tags: list(post.canonicalIngredients) },
      { title: '해시태그', tags: list(post.hashtags).slice(0, 20).map((h) => (h.startsWith('#') ? h : `#${h}`)) },
    ]),
  };
}

/**
 * 오가닉 상세의 제목·본문 — 같은 글이 두 번 서지 않게 가른다.
 *
 * 소셜 글(인스타·틱톡·X·스레드)은 글 전체가 `title` 하나에 실리고 `content` 가 비어 있다. 인용 글도
 * 같다 — `content` 는 원글이라 ingest 가 비우고, 원글은 원글 상자가 따로 그린다. 그때는 본문 하나로 세운다.
 * 제목이 본문의 앞머리(끝의 말줄임은 빼고 · 공백 차이 무시)와 같아도 본문 하나다.
 * 둘이 다른 글(유튜브 제목+설명 · 블로그 제목+본문)일 때만 제목과 본문이 따로 선다.
 * @returns `heading` 이 빈 문자열이면 제목 줄을 그리지 않는다
 */
export function splitDetailText(title: string, content: string): { heading: string; body: string } {
  const t = title.trim();
  const c = content.trim();
  if (!c) return { heading: '', body: t };
  if (!t) return { heading: '', body: c };
  const nt = comparable(t);
  const nc = comparable(c);
  if (nc.startsWith(nt)) return { heading: '', body: c };
  if (nt.startsWith(nc)) return { heading: '', body: t };
  return { heading: t, body: c };
}

/** 앞머리 대조용 — 공백을 한 칸으로 접고 끝의 말줄임(`…` · `...`)을 뗀다 */
function comparable(s: string): string {
  return s.replace(/\s+/g, ' ').replace(/(…|\.{3})$/, '').trim();
}
